import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import Layout from '../../../components/feature/Layout';
import PageBanner from '../../../components/feature/PageBanner';
import { Link } from 'react-router-dom';

const steps = [
  { icon: 'ri-chat-check-line', step: '01', title: 'Free Consultation', desc: 'We discuss your vision, walk the space, and understand what you want to achieve with your renovation.' },
  { icon: 'ri-draft-line', step: '02', title: 'Design & Planning', desc: 'Our team drafts detailed plans with material selections, timelines, and transparent pricing before a single nail is driven.' },
  { icon: 'ri-hammer-line', step: '03', title: 'Expert Execution', desc: 'Licensed craftsmen handle every phase — framing, drywall, flooring, trim, paint — with strict quality control throughout.' },
  { icon: 'ri-star-line', step: '04', title: 'Final Walkthrough', desc: 'We do a thorough walk-through with you, address any punch-list items, and don\'t consider the job done until you\'re thrilled.' },
];

const interiorFeatures = [
  'Kitchen Remodels & Upgrades', 'Bathroom Renovations', 'Basement Finishing', 'Flooring Installation',
  'Drywall & Painting', 'Trim & Millwork', 'Door & Window Replacement', 'Interior Lighting',
];

const exteriorFeatures = [
  'Siding Installation & Repair', 'Exterior Painting', 'Deck & Porch Building', 'Soffit & Fascia Work',
  'Roofing Repairs', 'Exterior Door Replacement', 'Gutter Installation', 'Curb Appeal Upgrades',
];

function SectionHeader({ label, title, accentWord, desc }: { label: string; title: string; accentWord?: string; desc: string }) {
  const { ref, visible } = useScrollAnimation();
  const renderTitle = () => {
    if (!accentWord) return title;
    const parts = title.split(new RegExp(`(${accentWord})`, 'i'));
    return parts.map((part, i) =>
      part.toLowerCase() === accentWord?.toLowerCase()
        ? <span key={i} className="text-primary">{part}</span>
        : part
    );
  };
  return (
    <div ref={ref} className={`text-center mb-12 sm:mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="flex items-center justify-center gap-3 mb-3">
        <div className="w-8 h-0.5 bg-primary" />
        <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">{label}</span>
        <div className="w-8 h-0.5 bg-primary" />
      </div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 leading-tight tracking-tight mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {renderTitle()}
      </h2>
      <p className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">{desc}</p>
    </div>
  );
}

export default function RenovationsPage() {
  const { ref: featRef, visible: featVis } = useScrollAnimation();
  const { ref: stepsRef, visible: stepsVis } = useScrollAnimation();

  return (
    <Layout>
      <PageBanner
        title="Interior & Exterior Renovations"
        subtitle="From kitchen remodels to full exterior transformations — we bring your vision to life with precision craftsmanship and honest pricing."
        breadcrumb="Renovations"
        imageUrl="https://public.readdy.ai/ai/img_res/894ae194e4db23e45fa307a148537e1d.jpg"
        accentWord="Renovations"
      />

      {/* Overview */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <SectionHeader
            label="What We Do"
            title="Complete Renovation Solutions"
            accentWord="Renovation"
            desc="Whether you're updating a single bathroom or taking on a whole-house transformation, Downs Construction brings the same level of care and craftsmanship to every project."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div ref={featRef} className={`transition-all duration-700 ${featVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="mb-7 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-black uppercase text-gray-900 mb-4 sm:mb-5 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <span className="text-primary">Interior</span> Services
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {interiorFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-3 group">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-primary text-base group-hover:text-primary-mid transition-colors duration-200"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-black uppercase text-gray-900 mb-4 sm:mb-5 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <span className="text-primary">Exterior</span> Services
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {exteriorFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-3 group">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-primary text-base group-hover:text-primary-mid transition-colors duration-200"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={`transition-all duration-700 delay-200 ${featVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                <div className="w-full h-80 sm:h-96 rounded-lg overflow-hidden img-zoom">
                  <img src="https://readdy.ai/api/search-image?query=modern%20luxury%20kitchen%20renovation%20with%20white%20marble%20countertops%2C%20custom%20cabinetry%2C%20stainless%20steel%20appliances%2C%20pendant%20lighting%2C%20hardwood%20floors%2C%20clean%20and%20elegant%20finish%2C%20professionally%20remodeled%20residential%20kitchen&width=700&height=500&seq=ren02&orientation=landscape" alt="Kitchen renovation by Downs Construction" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute -bottom-6 -left-6 w-40 sm:w-48 h-36 sm:h-40 rounded-lg overflow-hidden border-4 border-white img-zoom">
                  <img src="https://readdy.ai/api/search-image?query=beautiful%20renovated%20bathroom%20with%20walk%20in%20shower%2C%20white%20tile%2C%20modern%20fixtures%2C%20clean%20professional%20renovation%2C%20neutral%20tones%2C%20elegant%20design&width=300&height=250&seq=ren03&orientation=portrait" alt="Bathroom renovation" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute -top-4 -right-4 bg-primary text-white p-4 rounded-lg">
                  <div className="text-3xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>29+</div>
                  <div className="text-xs uppercase tracking-wider font-bold text-primary-light">Years Exp.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-primary-dark py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #2A5BA8 0%, transparent 50%)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">Our Process</span>
              <div className="w-8 h-0.5 bg-primary-light" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              How We <span className="text-primary-light">Work</span>
            </h2>
          </div>
          <div ref={stepsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {steps.map((step, i) => (
              <div
                key={step.step}
                className="relative bg-white/5 border border-white/10 rounded-lg p-6 sm:p-7 hover-card hover:border-primary-mid/40"
                style={{ transitionDelay: `${i * 120}ms`, opacity: stepsVis ? 1 : 0, transform: stepsVis ? 'translateY(0)' : 'translateY(32px)' }}
              >
                <div className="text-6xl sm:text-7xl font-black text-white/5 absolute top-4 right-4 leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{step.step}</div>
                <div className="w-12 h-12 flex items-center justify-center bg-primary/40 rounded-lg mb-5">
                  <i className={`${step.icon} text-primary-light text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{step.title}</h3>
                <p className="text-blue-200/60 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary to-primary-mid">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Ready to Transform Your Space?
          </h2>
          <p className="text-blue-100/80 mb-8 text-sm sm:text-base max-w-xl mx-auto">Get your free, no-obligation estimate today. Our team is ready to bring your renovation vision to life.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              Get Free Estimate
            </Link>
            <a href="tel:269-599-4913" className="border-2 border-white/60 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              (269) 599-4913
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
